'use client';

import { ReactElement, useEffect, useRef, useState } from 'react';
import { motion, TargetAndTransition } from 'motion/react';

/**
 * Data structure for an individual marquee carousel item.
 */
export interface IMarqueeCarouselItem {
  /** The icon or visual element to display. */
  icon: ReactElement;
  /** Unique identifier for the item. */
  id: string;
  /** Accessible name or title for the item. */
  name: string;
}

/**
 * Properties for the MarqueeCarousel component.
 */
interface IMarqueeCarouselProps {
  /**
   * How fast the carousel should move from left to right.
   */
  duration?: number;
  /**
   * The items to be shared
   */
  items: IMarqueeCarouselItem[];
}

/**
 * -----------------------------------------------------------------------------
 * It displays a carousel of items that move from right to left on the screen.
 * For example as displayed in the OurCollaboratorSection component as a list
 * of logos of the collaborators.
 */
export function MarqueeCarousel({
  duration = 50,
  items,
}: IMarqueeCarouselProps) {
  const iconRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (iconRef.current) {
      setWidth(iconRef.current.offsetWidth);
    }
  }, [iconRef]);

  const animate: TargetAndTransition = {
    x: [0, -width],
    transition: { duration, repeat: Infinity, ease: 'linear' },
  };

  return (
    <motion.div animate={animate} className="flex w-full flex-nowrap py-6">
      <div
        ref={iconRef}
        className="flex gap-20 pe-20 max-sm:gap-10 max-sm:pe-10"
      >
        {[...items].map((item) => (
          <div key={item.id} title={item.name}>
            {item.icon}
          </div>
        ))}
      </div>

      <div className="flex gap-20 max-sm:gap-10">
        {[...items].map((item) => (
          <div key={item.id} title={item.name}>
            {item.icon}
          </div>
        ))}
      </div>
    </motion.div>
  );
}
